// The lowest layer: one JSON request to the API, no tokens involved.
import {ApiErrorCode, ErrorParams, FieldError, isApiErrorCode, isFieldErrorCode} from "./errors";
import {noteRequest} from "./catalog";

// What the API's error response says (src/api/errors.ts): a code, never wording. errorMessage (ErrorAlert) turns it
// into the site's words.
export interface ApiErrorDetail {
    code?: ApiErrorCode;
    params?: ErrorParams;
    fields?: FieldError[];
    // On a 500: for the user to quote to support, matching the API's logs.
    requestId?: string;
}

export class ApiError extends Error {
    public readonly code?: ApiErrorCode;
    public readonly params?: ErrorParams;
    public readonly fields: FieldError[];
    public readonly requestId?: string;

    // status 0: the API couldn't be reached.
    public constructor(public readonly status: number, detail: ApiErrorDetail = {}) {
        super(`The server answered ${status}${detail.code ? ` (${detail.code})` : ''}`);
        this.code = detail.code;
        this.params = detail.params;
        this.fields = detail.fields ?? [];
        this.requestId = detail.requestId;
    }
}

// Fired on window when the API answers that the site is down for maintenance (SiteStatusProvider shows the message).
export const MAINTENANCE_EVENT = 'tristar:maintenance';

// How many requests are waiting for the API's answer: ApiActivityBar shows one's on its way (a cold start can take a few
// seconds). StatsApi's fire-and-forget hits don't go through here, so they don't count.
let inFlight = 0;
const activityListeners = new Set<() => void>();
export const apiActivity = {
    busy: () => inFlight > 0,
    subscribe(listener: () => void) {
        activityListeners.add(listener);
        return () => { activityListeners.delete(listener); };
    }
};

function track(change: 1 | -1) {
    inFlight += change;
    activityListeners.forEach(listener => listener());
}

// credentials: 'include' only for the token endpoint, whose HttpOnly cookie holds the refresh token.
export async function send<T>(method: string, path: string, body?: unknown, accessToken?: string, options?: { credentials?: RequestCredentials, headers?: Record<string, string> }): Promise<T> {
    track(1);
    try {
        return await request<T>(method, path, body, accessToken, options);
    } finally {
        track(-1);
    }
}

async function request<T>(method: string, path: string, body: unknown, accessToken: string | undefined, options: { credentials?: RequestCredentials, headers?: Record<string, string> } | undefined): Promise<T> {
    const headers: Record<string, string> = { ...options?.headers };
    if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
    }
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }
    let response: Response;
    try {
        response = await fetch(`${API_BASE}${path}`, {
            method,
            headers,
            body: body === undefined ? undefined : JSON.stringify(body),
            credentials: options?.credentials,
            // Always the API's answer now, never the browser's copy of an earlier one: the API's data changes under the
            // page (an administrator's list, a sending email's count), and Safari reuses GETs that don't forbid it.
            cache: 'no-store'
        });
    } catch {
        throw new ApiError(0);
    }
    if (!response.ok) {
        const detail = await readErrorDetail(response);
        if (response.status === 503 && detail.code === 'maintenance') {
            window.dispatchEvent(new Event(MAINTENANCE_EVENT));
        }
        throw new ApiError(response.status, detail);
    }
    noteRequest(method, path);
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
}

// { error, params?, fields?, requestId? }, keeping only what's well-formed: anything else is left to the status.
// The API's 503 for maintenance mode used to be { Error: 'Maintenance' }: still understood until it's redeployed.
async function readErrorDetail(response: Response): Promise<ApiErrorDetail> {
    let body: any;
    try {
        body = await response.json();
    } catch {
        return {};
    }
    if (body?.Error === 'Maintenance') {
        return { code: 'maintenance' };
    }
    const isParams = (x: unknown): x is ErrorParams => !!x && typeof x === 'object' && !Array.isArray(x);
    return {
        code: isApiErrorCode(body?.error) ? body.error : undefined,
        params: isParams(body?.params) ? body.params : undefined,
        fields: Array.isArray(body?.fields)
            ? body.fields.filter((x: any) => typeof x?.field === 'string' && isFieldErrorCode(x?.error))
                .map((x: any) => ({ field: x.field, error: x.error, ...(isParams(x.params) ? { params: x.params } : {}) }))
            : undefined,
        requestId: typeof body?.requestId === 'string' ? body.requestId : undefined
    };
}
